import { lazy, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { ApplicationContextProvider } from '../../context/ApplicationContext';
import { UserType, ApplicationType, ApplicationStatus } from '../data/collections';
import { Pages } from '../data/Constants';
import { paths } from './paths';
import { generatePath } from './routeUtils';
import { RouteGuard } from './router';

// Route-level code splitting (PF-5): keep guards/context eager; lazy-load page modules.
const New = lazy(() => import('../../pages/new/New'));
const Home = lazy(() => import('../../pages/home/Home'));
const List = lazy(() => import('../../pages/list/List'));
const Edit = lazy(() => import('../../pages/edit/Edit'));
const Login = lazy(() => import('../../pages/login/Login'));
const ChangePassword = lazy(() => import('../../pages/login/ChangePassword'));
const LegalPage = lazy(() => import('../../pages/legal/LegalPage'));
const MobileApp = lazy(() => import('../../pages/mobile/MobileApp'));
const Apply = lazy(() => import('../../pages/apply/Apply'));
const Rsvp = lazy(() => import('../../pages/interviews/RSVP'));
const Logout = lazy(() => import('../../pages/logout/Logout'));
const Review = lazy(() => import('../../pages/review/Review'));
const Single = lazy(() => import('../../pages/single/Single'));
const Redirect = lazy(() => import('../../pages/redirect/Redirect'));
const Register = lazy(() => import('../../pages/register/Register'));
const Unsubscribe = lazy(() => import('../../pages/unsub/Unsubscribe'));
const SiteSettings = lazy(() => import('../../pages/settings/Settings'));
const MemberDash = lazy(() => import('../../pages/memberDash/MemberDash'));
const AdminLayout = lazy(() => import('../../components/layout/AdminLayout'));
const AccessDenied = lazy(() => import('../../components/layout/AccessDenied'));
const ContactCenter = lazy(() => import('../../pages/contactCenter/ContactCenter'));
const ManualUploader = lazy(() => import('../../components/forms/members/ManualUploader'));
const CycleExpenses = lazy(() => import('../../components/forms/members/CycleExpenses'));
const ApplicationController = lazy(() => import('../../components/forms/applications/ApplicationController'));
const PDFApplication = lazy(() =>
	import('../../components/pdf/PDFApplication').then((m) => ({ default: m.PDFApplication })),
);
const UploadCenter = lazy(() => import('../../pages/uploadCenter/UploadCenter'));
const Onboard = lazy(() => import('../../pages/register/Onboard'));
const NotFound = lazy(() => import('../../components/layout/NotFound'));
const WaitingRoom = lazy(() => import('../../pages/interviews/WaitingRoom'));
const InterviewLayout = lazy(() => import('../../components/layout/InterviewLayout'));
const InterviewRoom = lazy(() => import('../../pages/interviews/InterviewRoom'));
const DeliberationRoom = lazy(() => import('../../pages/interviews/DeliberationRoom'));
const MemberChatInbox = lazy(() => import('../../chat/MemberChatInbox'));

export interface SiteManifestEntry {
	urlKey?: string;
	path: string;
	element: ReactNode;
	index?: boolean;
	children?: SiteManifestEntry[];
}

export const siteManifest: SiteManifestEntry[] = [
	{ urlKey: Pages.root, path: paths.root, element: <Home />, index: true },
	{
		urlKey: Pages.login,
		path: paths.login,
		element: (
			<RouteGuard allowUnauthed={true}>
				<Login />
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.changePassword,
		path: paths.changePassword,
		element: (
			<RouteGuard>
				<ChangePassword />
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.privacy,
		path: paths.privacy,
		element: (
			<RouteGuard allowUnauthed={true}>
				<LegalPage kind='privacy' />
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.terms,
		path: paths.terms,
		element: (
			<RouteGuard allowUnauthed={true}>
				<LegalPage kind='terms' />
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.mobileApp,
		path: paths.mobileApp,
		element: (
			<RouteGuard allowUnauthed={true}>
				<MobileApp />
			</RouteGuard>
		),
	},
	{ urlKey: Pages.logout, path: paths.logout, element: <Logout /> },
	{
		urlKey: Pages.redirect,
		path: paths.redirect,
		element: (
			<RouteGuard>
				<Redirect />
			</RouteGuard>
		),
	},
	{ urlKey: Pages.home, path: paths.home, element: <Home /> },

	{
		urlKey: Pages.applyHome,
		path: paths.apply,
		element: (
			<RouteGuard allowedRoles={[UserType.applicant, UserType.both]}>
				<Apply />
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.createApplication,
		path: paths.createApplication,
		element: (
			<RouteGuard allowedRoles={[UserType.applicant, UserType.both]}>
				<ApplicationContextProvider>
					<ApplicationController />
				</ApplicationContextProvider>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.updateApplication,
		path: paths.updateApplication,
		element: (
			<RouteGuard allowedRoles={[UserType.applicant, UserType.both]}>
				<ApplicationContextProvider>
					<ApplicationController />
				</ApplicationContextProvider>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.reviewApp,
		path: paths.reviewApp,
		element: (
			<RouteGuard allowedRoles={[UserType.applicant, UserType.both]}>
				<Review />
			</RouteGuard>
		),
	},

	{
		urlKey: Pages.registerApplicant,
		path: paths.registerApplicant,
		element: (
			<RouteGuard allowedRoles={[UserType.member, UserType.both]} permissions={['admin']} allowUnauthed={true}>
				<Register />
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.registerMember,
		path: paths.registerMember,
		element: (
			<RouteGuard allowedRoles={[UserType.member, UserType.both]} permissions={['admin']} allowUnauthed={true}>
				<Onboard />
			</RouteGuard>
		),
	},

	{
		urlKey: Pages.allApplicants,
		path: paths.allApplicants,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={UserType.applicant} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.viewApplicant,
		path: paths.viewApplicant,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<Single type={UserType.applicant} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.editApplicant,
		path: paths.editApplicant,
		element: (
			<RouteGuard permissions={['login', 'admin']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<Edit type={UserType.applicant} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.newApplicant,
		path: paths.newApplicant,
		element: (
			<RouteGuard permissions={['login', 'admin']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<New type={UserType.applicant} />
				</AdminLayout>
			</RouteGuard>
		),
	},

	{
		urlKey: Pages.allMembers,
		path: paths.allMembers,
		element: (
			<RouteGuard permissions={['login', 'members']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={UserType.member} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.viewMember,
		path: paths.viewMember,
		element: (
			<RouteGuard permissions={['login', 'members']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<Single type={UserType.member} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.newMember,
		path: paths.newMember,
		element: (
			<RouteGuard permissions={['login', 'admin', 'members']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<New type={UserType.member} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.editMember,
		path: paths.editMember,
		element: (
			<RouteGuard permissions={['login', 'admin', 'members']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<Edit type={UserType.member} />
				</AdminLayout>
			</RouteGuard>
		),
	},

	{
		urlKey: Pages.memberDash,
		path: paths.memberDash,
		element: (
			<RouteGuard permissions={['login']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<MemberDash />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.contactCenter,
		path: paths.contactCenter,
		element: (
			<RouteGuard permissions={['login', 'push']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<ContactCenter />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.siteSettings,
		path: paths.siteSettings,
		element: (
			<RouteGuard permissions={['login', 'site']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<SiteSettings />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.chatInbox,
		path: paths.chatInbox,
		element: (
			<RouteGuard permissions={['chat']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<MemberChatInbox />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.financeCenter,
		path: paths.financeCenter,
		element: (
			<RouteGuard permissions={['login', 'finances']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type="legacyFinances" />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.cycleExpenses,
		path: paths.cycleExpenses,
		element: (
			<RouteGuard permissions={['login', 'finances']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<CycleExpenses />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.adminAccessDenied,
		path: paths.adminAccessDenied,
		element: (
			<AdminLayout>
				<AccessDenied message='You do not have permission to access this page.' />
			</AdminLayout>
		),
	},
	{
		urlKey: Pages.scheduling,
		path: paths.scheduling,
		element: (
			<RouteGuard permissions={['login', 'interviews.canSchedule']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type='scheduler' />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.interviewDash,
		path: paths.interviewDash,
		element: (
			<RouteGuard permissions={['login', 'interviews.canAccess']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type='interviews' />
				</AdminLayout>
			</RouteGuard>
		),
	},

	{
		urlKey: Pages.allRequests,
		path: paths.allRequests,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type='requests' />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.editRequest,
		path: paths.editRequest,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<Edit type='Request' />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.newRequest,
		path: paths.newRequest,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<New type='Request' />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{ urlKey: Pages.requests, path: paths.requests, element: <UploadCenter /> },
	{
		urlKey: 'manualUpload',
		path: paths.manualUpload,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<ManualUploader />
				</AdminLayout>
			</RouteGuard>
		),
	},

	{
		urlKey: Pages.inbox,
		path: paths.inbox,
		element: (
			<RouteGuard permissions={['login', 'email']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type="inbox" />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.viewEmail,
		path: paths.viewEmail,
		element: (
			<RouteGuard permissions={['login', 'email']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<Single type="email" />
				</AdminLayout>
			</RouteGuard>
		),
	},
    {
        urlKey: 'composeEmail',
        path: paths.composeEmail,
        element: (
            <RouteGuard permissions={['login', 'email']} allowedRoles={[UserType.member, UserType.both]}>
                <AdminLayout>
                    <New type="email" />
                </AdminLayout>
            </RouteGuard>
        ),
    },

	{
		urlKey: Pages.allAppsInYear,
		path: paths.allAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type='year' />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.newApps,
		path: paths.newApps,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationType.newApplication} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.returningApps,
		path: paths.returningApps,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationType.returningGrant} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.scholarshipApps,
		path: paths.scholarshipApps,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationType.scholarship} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.newAppsInYear,
		path: paths.newAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationType.newApplication} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.returningAppsInYear,
		path: paths.returningAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationType.returningGrant} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.scholarshipAppsInYear,
		path: paths.scholarshipAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationType.scholarship} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.allApps,
		path: paths.allApps,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type='applications' />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.viewApp,
		path: paths.viewApp,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<Single type='application' />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.exportApp,
		path: paths.exportApp,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<PDFApplication />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.archives,
		path: paths.archives,
		element: (
			<RouteGuard permissions={['login', 'applications', 'archives']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type='archives' />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.completedApps,
		path: paths.completedApps,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.completed} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.eligibleApps,
		path: paths.eligibleApps,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.eligible} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.invitedApps,
		path: paths.invitedApps,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.invited} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.awardedApps,
		path: paths.awardedApps,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.awarded} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.deletedApps,
		path: paths.deletedApps,
		element: (
			<RouteGuard permissions={['login', 'admin', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.deleted} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.rejectedApps,
		path: paths.rejectedApps,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type='rejected' />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.incompleteApps,
		path: paths.incompleteApps,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.incomplete} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.completedAppsInYear,
		path: paths.completedAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.completed} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.eligibleAppsInYear,
		path: paths.eligibleAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.eligible} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.invitedAppsInYear,
		path: paths.invitedAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.invited} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.awardedAppsInYear,
		path: paths.awardedAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.awarded} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.deletedAppsInYear,
		path: paths.deletedAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'admin', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.deleted} />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.rejectedAppsInYear,
		path: paths.rejectedAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type='rejected' />
				</AdminLayout>
			</RouteGuard>
		),
	},
	{
		urlKey: Pages.incompleteAppsInYear,
		path: paths.incompleteAppsInYear,
		element: (
			<RouteGuard permissions={['login', 'applications']} allowedRoles={[UserType.member, UserType.both]}>
				<AdminLayout>
					<List type={ApplicationStatus.incomplete} />
				</AdminLayout>
			</RouteGuard>
		),
	},

	{
		urlKey: Pages.interviewCallSession,
		path: '/interviews',
		element: <InterviewLayout />,
		children: [
			{
				urlKey: Pages.waitingRoom,
				path: 'waiting-room/:interviewId',
				element: (
					<RouteGuard permissions={['interviews.canAccess']} allowedRoles={[UserType.applicant, UserType.member, UserType.both]}>
						<WaitingRoom />
					</RouteGuard>
				),
			},
			{
				urlKey: Pages.interviewRoom,
				path: 'interview-room/:interviewId',
				element: (
					<RouteGuard allowedRoles={[UserType.applicant, UserType.member, UserType.both]} permissions={['interviews.canAccess']}>
						<InterviewRoom />
					</RouteGuard>
				),
			},
			{
				urlKey: Pages.deliberationRoom,
				path: 'deliberation-room',
				element: (
					<RouteGuard permissions={['interviews.canAccess']} allowedRoles={[UserType.member, UserType.both]}>
						<DeliberationRoom />
					</RouteGuard>
				),
			},
		],
	},
	{ urlKey: Pages.rsvp, path: paths.rsvp, element: <Rsvp /> },

	{
		urlKey: Pages.unsubscribe,
		path: paths.unsubscribe,
		element: <Unsubscribe />,
	},
	{
		path: '/members/applicants',
		element: <Navigate to={generatePath(paths.allApplicants)} replace />,
	},
	{ urlKey: Pages.notFound, path: paths.notFound, element: <NotFound /> },
	{
		urlKey: Pages.caAccessDenied,
		path: paths.caAccessDenied,
		element: <AccessDenied message='Access denied.' />,
	},
	{ urlKey: Pages.caRoot, path: paths.caRoot, element: <NotFound /> },
];
